import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Avatar,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Autorenew,
} from "@mui/icons-material";

const TaskProgressCard = ({ taskProgress, isEmployeeScreen = false }) => {
  const theme = useTheme();

  if (!taskProgress.active) return null;

  // Configuration for colors and icons
  const getStatusConfig = (status) => {
    switch (status) {
      case "SUCCESS":
        return {
          chipColor: theme.palette.success.dark,
          chipBg: theme.palette.success.light,
          barColor: "#10B981", // Green
          icon: <CheckCircle fontSize="small" />,
          label: "Completed",
        };
      case "FAILURE":
        return {
          chipColor: theme.palette.error.dark,
          chipBg: theme.palette.error.light,
          barColor: theme.palette.error.main, // Red
          icon: <ErrorIcon fontSize="small" />,
          label: "Failed",
        };
      default:
        return {
          chipColor: "#000000", // Black text/icon
          chipBg: theme.palette.grey[200],
          barColor: "#10B981", // Green progress bar
          icon: (
            <Autorenew
              fontSize="small"
              sx={{ animation: "spin 2s linear infinite" }}
            />
          ),
          label: "In Progress",
        };
    }
  };

  const statusConfig = getStatusConfig(taskProgress.status);

  return (
    <Card
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ pb: 2 }}>
        {/* Header: Title and Status Chip */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {taskProgress.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {taskProgress.subtitle ?? (
                <>
                  Updating <strong>{taskProgress.shiftCategoryName}</strong>{" "}
                  {!isEmployeeScreen && "category"}
                </>
              )}
            </Typography>
          </Box>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="small"
            sx={{
              bgcolor: statusConfig.chipBg,
              color: statusConfig.chipColor,
              fontWeight: 700,
              border: "1px solid transparent",
              "& .MuiChip-icon": { color: statusConfig.chipColor },
            }}
          />
        </Stack>

        {/* User Info Row */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          {/* AVATAR UPDATE: Uses image_url if present, otherwise falls back to initials */}
          <Avatar
            src={taskProgress.image_url}
            alt={taskProgress.initiatedBy}
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.grey[300],
              fontSize: 14,
              color: "#333",
            }}
          >
            {taskProgress.initiatedBy?.charAt(0) || "U"}
          </Avatar>

          <Typography variant="body2">
            Initiated by <strong>{taskProgress.initiatedBy}</strong>
          </Typography>
        </Stack>

        {/* Progress Section */}
        <Box mb={1}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              PROGRESS
            </Typography>
            <Typography variant="caption" fontWeight={600} color="text.primary">
              {taskProgress.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={taskProgress.progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: theme.palette.grey[100],
              "& .MuiLinearProgress-bar": {
                bgcolor: statusConfig.barColor,
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </CardContent>

      {/* Footer: Meta Data */}
      <Box
        sx={{
          bgcolor: "#F8FAFC",
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          divider={<Divider orientation="vertical" flexItem />}
        >
          {taskProgress.estimatedTime && (
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime sx={{ fontSize: 16, color: "text.secondary" }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Remaining
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {taskProgress.estimatedTime}
                </Typography>
              </Box>
            </Stack>
          )}

          {taskProgress.duration && (
            <Box>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                Elapsed
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {taskProgress.duration}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Last Update
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {taskProgress.updated_at}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Card>
  );
};

export default TaskProgressCard;
